import {FC, useMemo} from 'react';
import dayjs from 'dayjs';
import {getDatesDifferenceInSeconds} from '../../hooks/useTimer';
import {calculateAndFormatTimeMMSS} from '../processes/helpers';
import LiveTimeProgressBar
  from '../common/time_progress_bar/LiveTimeProgressBar';
import {
  AlertSettingsProps,
  AlertSettingsResponseProps
} from '../settings/alert_alarm/types';
import {
  ReactComponent as CancelledTaskIcon
} from '../../assets/images/cancelled-task.svg';
import {Link} from 'react-router-dom';
import {dateFormat} from '../../constants/constants';
import ProcessExpiredTime from '../processes/ProcessExpiredTime';
import {ProcessCode} from '../processes/types';
import classNames from 'classnames';
import processesRoutes from '../processes/routes';
import {
  PatientProcessProps,
  PatientSurveyProps,
  PatientSurveysResponse
} from './types';
import surveysRoutes from '../surveys/routes';
import {useTranslation} from 'react-i18next';

type PatientProcessesElementProps = {
  patientProcessItem: PatientProcessProps,
  processesAlertData: Nullable<AlertSettingsResponseProps>,
  patientSurveysData: Nullable<PatientSurveysResponse>,
}

const PatientProcessesElement: FC<PatientProcessesElementProps> = ({
  patientProcessItem,
  processesAlertData,
  patientSurveysData
}) => {
  const {t} = useTranslation('patients');

  const {
    id,
    callSheet,
    completedAt,
    startedAt,
    status,
    targetTime,
    type
  } = patientProcessItem || {};

  const processTypeAlertSettings = processesAlertData?.length &&
    processesAlertData.find((item: AlertSettingsProps) =>
      item.processType === type.code) || null;

  const matchedSurvey = patientSurveysData?.length &&
    patientSurveysData.find((item: PatientSurveyProps) =>
      item.callSheetId === callSheet.id) || null;

  const completedInTime = useMemo(() => {
    const calculatedGoalTimeInSeconds = startedAt && completedAt ?
      getDatesDifferenceInSeconds(startedAt, completedAt) : null;
    return calculateAndFormatTimeMMSS(calculatedGoalTimeInSeconds);
  }, [startedAt, completedAt]);

  const formattedStartAt = dayjs(startedAt).format(dateFormat);

  return (
    <div className={classNames('running-processes-element',
        {'running': status === ProcessCode.RUNNING},
        {'cancelled': status === ProcessCode.TERMINATED})}
    >
      <div className="running-processes-element_name-wrapper">
        <Link
          to={processesRoutes.runningItem(id)}
          className="running-processes-element_name"
        >
          {type && type.description}
          {formattedStartAt && ` ${formattedStartAt}`}
        </Link>
      </div>
      {status === ProcessCode.TERMINATED ?
      (<CancelledTaskIcon height={25} width={25}/>) :
        (<>
          <div className="value-with-heading">
            <span className="heading">{t('completedInHeadingLabel')}</span>
            <span className="value">{completedInTime}</span>
          </div>
          <ProcessExpiredTime
            startedAt={startedAt}
            completedAt={completedAt}
            targetTime={targetTime}
            alertSettings={processTypeAlertSettings}
            timeFormat={calculateAndFormatTimeMMSS}
          />
          <LiveTimeProgressBar
            startedAt={startedAt}
            completedAt={completedAt}
            targetTime={targetTime}
            alertSettings={processTypeAlertSettings}
            isCurrentlyRunning={status === ProcessCode.RUNNING}
          />
          <div className="survey-btn-wrapper">
            {!!matchedSurvey &&
              <Link
                to={surveysRoutes.item(matchedSurvey.surveyId)}
                className="treatment-button"
              >
                {t('surveyFormBtnLabel')}
              </Link>}
          </div>
        </>)}
    </div>
  );
};

export default PatientProcessesElement;
